import { useId, useMemo, useState } from "react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import {
  btnIconCls,
  btnLinkCls,
  errorTextCls,
  helpTextCls,
  inputCls,
  labelCls,
} from "components/tw/classes";
import { vilniusTodayYmd } from "utils/vilnius-date";
import { toMonthInput } from "features/billing-period/billing-period";

type MonthPickerProps = {
  /** "YYYY-MM", or "" for empty */
  value: string;
  onChange: (value: string) => void;
  label: string;
  isRequired?: boolean;
  placeholder?: string;
  helpText?: string;
  error?: string;
  readOnly?: boolean;
  fullWidth?: boolean;
};

const MONTH_KEYS = Array.from({ length: 12 }, (_, i) => `month_short_${i + 1}`);

function parseYm(value: string): { year: number; month: number } | null {
  const match = /^(\d{4})-(\d{2})$/.exec(value || "");
  if (!match) return null;
  const month = Number(match[2]);
  if (month < 1 || month > 12) return null;
  return { year: Number(match[1]), month };
}

/**
 * Month-only picker ("which month does billing start"). A native
 * <input type="month"> can't be localized from the page — its popup follows the
 * browser's UI language — so the grid is rendered here, with month names from
 * the app's own translations.
 *
 * The panel is anchored and portaled to <body>, so a modal's scroll area cannot
 * clip it. `data-portal-id` keeps it usable inside a Polaris modal: Polaris's
 * focus trap ignores focus within `[data-portal-id]`; without it, clicking a
 * month would pull focus back into the modal and close the panel before the
 * click landed.
 */
export default function MonthPicker({
  value,
  onChange,
  label,
  isRequired = false,
  placeholder,
  helpText,
  error,
  readOnly = false,
  fullWidth = true,
}: MonthPickerProps) {
  const { t } = useTranslation();
  const id = useId();
  const selected = parseYm(value);
  const todayYm = useMemo(() => toMonthInput(vilniusTodayYmd()), []);
  const today = parseYm(todayYm);
  const [viewYear, setViewYear] = useState(
    () => selected?.year ?? today?.year ?? new Date().getFullYear()
  );

  const fieldError = error ?? (isRequired && !selected ? t("field_is_required") : "");
  const describedBy = fieldError ? `${id}-error` : helpText ? `${id}-help` : undefined;

  const fieldCls = clsx(
    inputCls,
    "flex items-center gap-2 text-left",
    readOnly ? "cursor-default" : "cursor-pointer"
  );
  const display = (
    <>
      <CalendarDays className="size-4 shrink-0 text-zinc-400" aria-hidden="true" />
      <span className={clsx("truncate", !value && "text-zinc-400")}>
        {value || placeholder || "YYYY-MM"}
      </span>
    </>
  );

  return (
    <div className={fullWidth ? "w-full" : "w-full max-w-[300px]"}>
      <label htmlFor={id} className={labelCls}>
        {label}
        {isRequired ? <span className="text-red-600"> *</span> : null}
      </label>

      {readOnly ? (
        <div
          id={id}
          className={fieldCls}
          aria-invalid={!!fieldError}
          aria-describedby={describedBy}
        >
          {display}
        </div>
      ) : (
        <Popover>
          <PopoverButton
            id={id}
            className={fieldCls}
            aria-invalid={!!fieldError}
            aria-describedby={describedBy}
            // Open on the chosen year, not wherever the grid was last left.
            onClick={() => setViewYear(selected?.year ?? today?.year ?? new Date().getFullYear())}
          >
            {display}
          </PopoverButton>
          <PopoverPanel
            anchor="bottom start"
            transition
            data-portal-id="month-picker"
            className="z-[1100] w-64 rounded-xl bg-white p-3 shadow-lg ring-1 ring-zinc-950/10 transition duration-100 ease-out [--anchor-gap:0.25rem] data-[closed]:opacity-0 dark:bg-zinc-800 dark:ring-white/10"
          >
            {({ close }) => {
              const pick = (next: string) => {
                onChange(next);
                close();
              };
              return (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      className={btnIconCls}
                      aria-label={t("previous_year")}
                      onClick={() => setViewYear((y) => y - 1)}
                    >
                      <ChevronLeft className="size-4" aria-hidden="true" />
                    </button>
                    <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                      {viewYear}
                    </span>
                    <button
                      type="button"
                      className={btnIconCls}
                      aria-label={t("next_year")}
                      onClick={() => setViewYear((y) => y + 1)}
                    >
                      <ChevronRight className="size-4" aria-hidden="true" />
                    </button>
                  </div>

                  <div className="grid grid-cols-4 gap-1.5">
                    {MONTH_KEYS.map((key, i) => {
                      const isSelected =
                        selected?.year === viewYear && selected.month === i + 1;
                      const isToday = today?.year === viewYear && today.month === i + 1;
                      return (
                        <button
                          key={key}
                          type="button"
                          aria-pressed={isSelected}
                          onClick={() => pick(`${viewYear}-${String(i + 1).padStart(2, "0")}`)}
                          className={clsx(
                            "rounded-md px-1 py-2 max-md:min-h-11 text-sm transition",
                            isSelected
                              ? "bg-indigo-600 font-semibold text-white"
                              : "text-zinc-800 hover:bg-zinc-100 dark:text-zinc-100 dark:hover:bg-zinc-700",
                            isToday && !isSelected && "ring-1 ring-inset ring-zinc-400"
                          )}
                        >
                          {t(key)}
                        </button>
                      );
                    })}
                  </div>

                  <div className="flex items-center justify-between pt-1">
                    {/* A required field has no empty state to clear to — the
                        caller's onChange would just ignore it, so don't offer
                        an action that silently does nothing. */}
                    {isRequired ? (
                      <span />
                    ) : (
                      <button type="button" className={btnLinkCls} onClick={() => pick("")}>
                        {t("clear")}
                      </button>
                    )}
                    <button type="button" className={btnLinkCls} onClick={() => pick(todayYm)}>
                      {t("this_month")}
                    </button>
                  </div>
                </div>
              );
            }}
          </PopoverPanel>
        </Popover>
      )}

      {fieldError ? (
        <p id={`${id}-error`} className={errorTextCls}>
          {fieldError}
        </p>
      ) : helpText ? (
        <p id={`${id}-help`} className={helpTextCls}>
          {helpText}
        </p>
      ) : null}
    </div>
  );
}
